import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, FileText, Mail, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import Modal from '../ui/Modal';
import { ClientApprovalStatus, Estimate, EstimateItemInput, WorkOrderVendor } from '../../types';

interface EstimatePanelProps {
  workOrderId: number;
  nteAmount?: number;
  vendors: WorkOrderVendor[];
  defaultClientEmail?: string;
  canConvertToInvoice?: boolean;
  onViewInvoice?: (invoiceId: number) => void;
}

const emptyLine = (): EstimateItemInput => ({
  description: '',
  quantity: 1,
  rate: 0,
  sortOrder: 0,
  isTaxable: true,
});

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const getStatusBadgeClass = (statusLabel: string) => {
  switch (statusLabel) {
    case 'Approved':
      return 'bg-green-100 text-green-800';
    case 'Submitted':
      return 'bg-blue-100 text-blue-800';
    case 'Rejected':
      return 'bg-red-100 text-red-800';
    case 'Converted':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const EstimatePanel: React.FC<EstimatePanelProps> = ({
  workOrderId,
  nteAmount,
  vendors,
  defaultClientEmail,
  canConvertToInvoice = false,
  onViewInvoice,
}) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [vendorId, setVendorId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<EstimateItemInput[]>([emptyLine()]);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [sendModalEstimateId, setSendModalEstimateId] = useState<number | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  const { data: estimatesResponse, isLoading } = useQuery({
    queryKey: ['estimates', workOrderId],
    queryFn: () => apiService.getEstimatesByWorkOrder(workOrderId),
  });

  const estimates = estimatesResponse?.data ?? [];

  const draftTotal = useMemo(
    () => lineItems.reduce((sum, item) => sum + item.quantity * item.rate, 0),
    [lineItems]
  );

  const draftExceedsNte = nteAmount != null && nteAmount > 0 && draftTotal > nteAmount;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['estimates', workOrderId] });
    queryClient.invalidateQueries({ queryKey: ['workOrder', workOrderId] });
    queryClient.invalidateQueries({ queryKey: ['workOrderInvoices', workOrderId] });
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const vendor = parseInt(vendorId, 10);
      if (!vendor) throw new Error('Select a vendor');
      const items = lineItems
        .filter((i) => i.description.trim())
        .map((item, index) => ({ ...item, sortOrder: index + 1 }));
      if (items.length === 0) throw new Error('Add at least one line item');

      const response = await apiService.createEstimate({
        workOrderId,
        vendorId: vendor,
        notes: notes || undefined,
        items,
      });
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to create estimate');
      }

      if (documentFile) {
        await apiService.uploadEstimateDocument(response.data.id, documentFile);
      }

      return response.data;
    },
    onSuccess: () => {
      toast.success('Estimate created');
      setShowForm(false);
      setVendorId('');
      setNotes('');
      setLineItems([emptyLine()]);
      setDocumentFile(null);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const submitMutation = useMutation({
    mutationFn: (id: number) => apiService.submitEstimate(id),
    onSuccess: (response) => {
      if (response.data?.nteComparison?.exceedsNte) {
        toast(response.data.nteComparison.alertMessage || 'Estimate exceeds NTE', { icon: '⚠️' });
      } else {
        toast.success('Estimate submitted');
      }
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Submit failed'),
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => apiService.approveEstimate(id),
    onSuccess: () => {
      toast.success('Estimate approved');
      invalidate();
    },
    onError: () => toast.error('Approve failed'),
  });

  const convertMutation = useMutation({
    mutationFn: (id: number) => apiService.convertEstimateToInvoice(id),
    onSuccess: (response) => {
      toast.success('Estimate converted to invoice');
      if (response.data?.id) {
        onViewInvoice?.(response.data.id);
      }
      invalidate();
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Convert failed'),
  });

  const sendClientApprovalMutation = useMutation({
    mutationFn: ({ estimateId, email, message }: { estimateId: number; email?: string; message?: string }) =>
      apiService.requestEstimateClientApproval(estimateId, {
        recipientEmail: email || undefined,
        customMessage: message || undefined,
      }),
    onSuccess: (response) => {
      toast.success(response.message || 'Approval request sent to client');
      setSendModalEstimateId(null);
      setCustomMessage('');
      invalidate();
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.message || error?.message || 'Failed to send approval request'),
  });

  const handleOpenSendModal = (estimateId: number, existingEmail?: string) => {
    setSendModalEstimateId(estimateId);
    setRecipientEmail(existingEmail || defaultClientEmail || '');
    setCustomMessage('');
  };

  const handleSendClientApproval = () => {
    if (!sendModalEstimateId) return;
    sendClientApprovalMutation.mutate({
      estimateId: sendModalEstimateId,
      email: recipientEmail.trim() || undefined,
      message: customMessage.trim() || undefined,
    });
  };

  const canInternalApprove = (estimate: Estimate) =>
    estimate.statusLabel === 'Submitted' &&
    (!estimate.exceedsNte || estimate.clientApprovalStatus === ClientApprovalStatus.Approved);

  const canSendClientApproval = (estimate: Estimate) =>
    estimate.statusLabel === 'Submitted' &&
    estimate.exceedsNte &&
    estimate.clientApprovalStatus !== ClientApprovalStatus.Pending &&
    estimate.clientApprovalStatus !== ClientApprovalStatus.Approved;

  const handleLineChange = (index: number, field: keyof EstimateItemInput, value: string | number | boolean) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleAddLine = () => setLineItems((prev) => [...prev, emptyLine()]);

  const handleRemoveLine = (index: number) => {
    setLineItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const renderNteAlert = (estimate: Estimate) => {
    if (!estimate.nteComparison?.exceedsNte && !estimate.exceedsNte) return null;
    const comparison = estimate.nteComparison;
    return (
      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">Exceeds NTE</p>
            <p>{comparison?.alertMessage || `Total ${formatCurrency(estimate.totalAmount)} exceeds NTE.`}</p>
            {comparison?.suggestedStatusName && (
              <p className="mt-1 text-xs">Suggested status: {comparison.suggestedStatusName}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="h-5 w-5" />
          Estimates
        </CardTitle>
        {!showForm && (
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus className="mr-1 h-4 w-4" />
            New Estimate
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {nteAmount != null && nteAmount > 0 && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            NTE: <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(nteAmount)}</span>
          </p>
        )}

        {showForm && (
          <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div className="mb-4 grid gap-4 md:grid-cols-2">
              <div>
                <Label>Vendor</Label>
                <Select value={vendorId} onValueChange={setVendorId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => (
                      <SelectItem key={v.vendorId} value={String(v.vendorId)}>
                        {v.vendorName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vendor PDF (optional)</Label>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,image/*"
                  onChange={(e) => setDocumentFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Line Items</Label>
              {lineItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2">
                  <Input
                    className="col-span-6"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                  />
                  <Input
                    className="col-span-2"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => handleLineChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                  />
                  <Input
                    className="col-span-3"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Rate"
                    value={item.rate}
                    onChange={(e) => handleLineChange(index, 'rate', parseFloat(e.target.value) || 0)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="col-span-1"
                    onClick={() => handleRemoveLine(index)}
                    aria-label="Remove line item"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={handleAddLine}>
                <Plus className="mr-1 h-4 w-4" />
                Add line
              </Button>
            </div>

            <div className="mt-3">
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Draft total: </span>
                <span className="font-semibold">{formatCurrency(draftTotal)}</span>
                {draftExceedsNte && (
                  <span className="ml-2 text-sm text-amber-600">Exceeds NTE</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
                  Save Draft
                </Button>
              </div>
            </div>
          </div>
        )}

        {isLoading && <p className="text-sm text-gray-500">Loading estimates...</p>}

        {!isLoading && estimates.length === 0 && !showForm && (
          <p className="text-sm text-gray-500">No estimates yet. Create one when the vendor submits a quote.</p>
        )}

        {estimates.map((estimate) => (
          <div key={estimate.id} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{estimate.vendorName || `Vendor #${estimate.vendorId}`}</span>
                <Badge className={getStatusBadgeClass(estimate.statusLabel)}>{estimate.statusLabel}</Badge>
                {estimate.exceedsNte && estimate.clientApprovalStatusLabel && estimate.clientApprovalStatus !== ClientApprovalStatus.None && (
                  <Badge variant="outline">{estimate.clientApprovalStatusLabel}</Badge>
                )}
              </div>
              <span className="font-semibold">{formatCurrency(estimate.totalAmount)}</span>
            </div>

            {estimate.items.length > 0 && (
              <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                {estimate.items.map((item) => (
                  <li key={item.id ?? item.description}>
                    {item.description} — {item.quantity} x {formatCurrency(item.rate)} = {formatCurrency(item.amount)}
                  </li>
                ))}
              </ul>
            )}

            {renderNteAlert(estimate)}

            {estimate.clientApprovalStatus === ClientApprovalStatus.Pending && estimate.clientApprovalEmail && (
              <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                Approval email sent to {estimate.clientApprovalEmail}
              </p>
            )}
            {estimate.clientApprovalStatus === ClientApprovalStatus.Rejected && estimate.clientRejectionReason && (
              <p className="mt-2 text-sm text-red-700 dark:text-red-300">
                Client rejected: {estimate.clientRejectionReason}
              </p>
            )}
            {estimate.clientApprovalStatus === ClientApprovalStatus.Approved && estimate.clientApprovedByName && (
              <p className="mt-2 text-sm text-green-700 dark:text-green-300">
                Client approved by {estimate.clientApprovedByName}
              </p>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              {estimate.statusLabel === 'Draft' && (
                <Button size="sm" onClick={() => submitMutation.mutate(estimate.id)} disabled={submitMutation.isPending}>
                  Mark Quote Received
                </Button>
              )}
              {canSendClientApproval(estimate) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenSendModal(estimate.id, estimate.clientApprovalEmail)}
                >
                  <Mail className="mr-1 h-4 w-4" />
                  Send to Client for Approval
                </Button>
              )}
              {estimate.statusLabel === 'Submitted' &&
                estimate.exceedsNte &&
                estimate.clientApprovalStatus === ClientApprovalStatus.Pending && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenSendModal(estimate.id, estimate.clientApprovalEmail)}
                >
                  <Mail className="mr-1 h-4 w-4" />
                  Resend Approval Email
                </Button>
              )}
              {canInternalApprove(estimate) && (
                <Button size="sm" onClick={() => approveMutation.mutate(estimate.id)} disabled={approveMutation.isPending}>
                  <CheckCircle className="mr-1 h-4 w-4" />
                  Internal Approval
                </Button>
              )}
              {(estimate.statusLabel === 'Submitted' || estimate.statusLabel === 'Approved') && !estimate.convertedInvoiceId && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => convertMutation.mutate(estimate.id)}
                  disabled={convertMutation.isPending || !canConvertToInvoice}
                  title={
                    !canConvertToInvoice
                      ? 'Complete documentation and move to Ready to Invoice first'
                      : undefined
                  }
                >
                  <Upload className="mr-1 h-4 w-4" />
                  Convert to Invoice
                </Button>
              )}
              {estimate.convertedInvoiceId && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onViewInvoice?.(estimate.convertedInvoiceId!)}
                >
                  View {estimate.convertedInvoiceNumber ?? `Invoice #${estimate.convertedInvoiceId}`}
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>

      <Modal
        isOpen={sendModalEstimateId != null}
        onClose={() => setSendModalEstimateId(null)}
        title="Send to Client for Approval"
        maxWidth="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            The client will receive an email with a secure link to approve or reject this estimate.
          </p>
          <div>
            <Label htmlFor="recipientEmail">Client email</Label>
            <Input
              id="recipientEmail"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="client@example.com"
            />
          </div>
          <div>
            <Label htmlFor="customMessage">Message (optional)</Label>
            <Textarea
              id="customMessage"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={3}
              placeholder="Add a note to include in the approval email"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSendModalEstimateId(null)}>
              Cancel
            </Button>
            <Button onClick={handleSendClientApproval} disabled={sendClientApprovalMutation.isPending}>
              {sendClientApprovalMutation.isPending ? 'Sending...' : 'Send Approval Email'}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default EstimatePanel;
